import { use, useMemo } from "react";
import dayjs from "dayjs";
import { GQL, Utils } from "@binale-tech/shared";
import { ReadinessContext } from "@domain/context/DataReadyness";
import { RecordsContext } from "@domain/context/accountingData/RecordsCtx";
import { PaymentsContext } from "@domain/context/accountingData/PaymentsProvider";
import { CompanyAccountingYearContext } from "@domain/context/CompanyContext";
import { bucketOpenPositions, type OpenPositionBucket, type OpenPositionItem } from "./openPositions";

const TOP_N = 5;

export const useOpenPositionEvents = (): { status: "loading" | "ready"; buckets: OpenPositionBucket[] } => {
    const { isReady } = use(ReadinessContext);
    const { allRecords } = use(RecordsContext);
    const payments = use(PaymentsContext);
    const { yearConfig } = use(CompanyAccountingYearContext);

    return useMemo(() => {
        if (!isReady || !payments.isLoaded) {
            return { status: "loading" as const, buckets: [] as OpenPositionBucket[] };
        }
        const today = new Date();
        const todayStartOfDay = dayjs(today).startOf("day");
        const items: OpenPositionItem[] = [];
        for (const record of allRecords.list || []) {
            if (record.draft || record.cancellation || !record.falligkeit) {
                continue;
            }
            // Review fix: this is a due/overdue view keyed on Fälligkeit, not booking date -
            // filtering on `record.date.getFullYear()` hid a prior-year invoice that's still
            // open and overdue today. Include the item when its Fälligkeit falls in the
            // selected year, OR (regardless of booking/Fälligkeit year) when it's still open
            // and overdue relative to today, so a carried-over overdue position from year-1
            // still surfaces in the "überfällig" bucket. Residual limitation: `allRecords`
            // is a sliding window of only [year-1, year, year+1] (see
            // useSaldoSources()/RecordsCtx) - an overdue item booked/due more than one year
            // back isn't loaded at all and can't be surfaced here.
            const falligkeitYear = record.falligkeit.getFullYear();
            const isOverdue = dayjs(record.falligkeit).isBefore(todayStartOfDay, "day");
            if (falligkeitYear !== yearConfig?.year && !isOverdue) {
                continue;
            }
            const pk = record.getProductKey();
            if (!Utils.PaymentUtils.isProductPaymentSource(pk)) {
                continue;
            }
            const open = record.getOpenBrutto(payments.recordRelation.get(record.key) ?? []);
            if (open === 0) {
                continue;
            }
            const side = pk === GQL.IProductKey.Deb ? "debitor" : "kreditor";
            const cc = record.getRecordCategoryCreditor();
            items.push({
                id: record.key,
                contactLabel: (
                    <div>
                        <span className="font-mono">{cc.getExtNumPrint(yearConfig?.kontoExt ?? 0)}</span> {cc.name}
                    </div>
                ),
                amountCents: open, // signed - preserves credit-note netting (see computeOpenPositions)
                date: record.falligkeit,
                side,
            });
        }
        return { status: "ready" as const, buckets: bucketOpenPositions(items, today, TOP_N) };
    }, [isReady, payments, allRecords, yearConfig]);
};
